import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import type { HostedAgentEvalRun } from "@/api/admin-api"

import { getHostedAgentEvalError } from "@/api/admin-api"
import { Play } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"

import { HostedAgentEvalCaseResultList } from "./hosted-agent-eval-case-results"
import {
  HostedAgentEvalRunError,
  HostedAgentEvalRunReadError,
  HostedAgentEvalRunStalled,
} from "./hosted-agent-eval-run-error"
import {
  HostedAgentEvalProvenanceCard,
  HostedAgentEvalRunSummary,
} from "./hosted-agent-eval-run-summary"
import {
  useAdminHostedAgentEvalRunQuery,
  useAdminHostedAgentEvalRunsQuery,
  useStartHostedAgentEvalRunMutation,
} from "../hooks/admin-queries"
import {
  planHostedAgentEvalPoll,
  planHostedAgentEvalRetry,
} from "../utils/hosted-agent-eval-run-state"

// Run panel (#3028). POST /eval-runs answers **202** with a `running`
// document — the turns happen in a background task — so this panel starts the
// suite and then polls GET /eval-runs/<id> until the status is terminal.
// `case_results` grows incrementally, so cases are drawn as they land.
//
// Two things this panel must not do:
//   * lose the run. The tab lives in a Radix `TabsContent` without
//     `forceMount`, so switching tabs unmounts the panel while the suite keeps
//     running server-side. The watched run is therefore DERIVED from the run
//     history (newest still-running run) and only seeded by the local start.
//   * poll for ever. A seeded 202 document says `running`; if the follow-up
//     GET keeps failing, that is the state the admin has to be told about,
//     not a spinner that never resolves.

type RunPanelProps = {
  agentId: string
  /** Empty = run the whole suite; non-empty = a subset, which stamps `partial`. */
  selectedCaseIds: string[]
  totalCases: number
  /** True when the agent has no enabled cases to run. */
  hasNoCases: boolean
}

export function HostedAgentEvalRunPanel({
  agentId,
  selectedCaseIds,
  totalCases,
  hasNoCases,
}: RunPanelProps) {
  const { t } = useTranslation()
  const startMutation = useStartHostedAgentEvalRunMutation()
  const [startedRunId, setStartedRunId] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [retrySeconds, setRetrySeconds] = useState<number | null>(null)

  const runsQuery = useAdminHostedAgentEvalRunsQuery(agentId)
  // Survive a remount: the suite outlives this component, so the newest run
  // still marked `running` is the one this panel was watching.
  const runningRunId = runsQuery.data?.find((candidate) => candidate.status === "running")?.id
  const watchedRunId = startedRunId ?? runningRunId ?? null

  const runQuery = useAdminHostedAgentEvalRunQuery(
    agentId,
    watchedRunId ?? undefined,
    !!watchedRunId
  )
  const run: HostedAgentEvalRun | undefined = runQuery.data
  const pollPlan = planHostedAgentEvalPoll(run, runQuery.failureCount)
  const isRunning = run?.status === "running" || startMutation.isPending

  // 429 carries `retry_after_seconds`. The countdown ticks in the unit the
  // wait is worth stating in — a daily cap is up to 24 h away, and ticking that
  // once a second for a day is both useless and wasteful. Reaching zero keeps
  // the alert (now reading "you can start again") instead of erasing it.
  useEffect(() => {
    if (retrySeconds === null || retrySeconds <= 0) return
    const plan = planHostedAgentEvalRetry(retrySeconds)
    const timer = setTimeout(
      () => setRetrySeconds((seconds) => Math.max(0, (seconds ?? 0) - plan.stepSeconds)),
      plan.tickMs
    )
    return () => clearTimeout(timer)
  }, [retrySeconds])

  const handleRun = async () => {
    setErrorCode(null)
    setErrorMessage(null)
    setRetrySeconds(null)
    try {
      const started = await startMutation.mutateAsync({
        agentId,
        caseIds: selectedCaseIds.length > 0 ? selectedCaseIds : undefined,
      })
      setStartedRunId(started.id)
    } catch (error) {
      const evalError = getHostedAgentEvalError(error)
      if (evalError) {
        setErrorCode(evalError.code)
        setErrorMessage(evalError.message)
        if (evalError.code === "eval_rate_limited") {
          setRetrySeconds(evalError.retryAfterSeconds ?? 60)
        }
      } else {
        setErrorCode("unknown")
        setErrorMessage(error instanceof Error ? error.message : null)
      }
    }
  }

  const blockedByRateLimit = retrySeconds !== null && retrySeconds > 0
  const subsetCount = selectedCaseIds.length

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          disabled={isRunning || blockedByRateLimit || hasNoCases}
          type="button"
          onClick={() => void handleRun()}
        >
          {isRunning ? <Spinner className="size-4" /> : <Play className="size-4" />}
          {subsetCount > 0
            ? t("adminDashboard.hostedAgents.evals.run.runSubset", {
                defaultValue: "Run {{n}} selected cases",
                n: subsetCount,
              })
            : t("adminDashboard.hostedAgents.evals.run.runAll", {
                defaultValue: "Run the whole suite",
              })}
        </Button>
        {subsetCount > 0 ? (
          <span className="text-muted-foreground text-xs">
            {t("adminDashboard.hostedAgents.evals.run.subsetNote", {
              defaultValue:
                "A subset run is marked partial and cannot be compared with a full-suite run.",
            })}
          </span>
        ) : (
          <span className="text-muted-foreground text-xs">
            {t("adminDashboard.hostedAgents.evals.run.allNote", {
              defaultValue: "{{n}} cases will run in the background.",
              n: totalCases,
            })}
          </span>
        )}
      </div>

      {hasNoCases ? (
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.hostedAgents.evals.run.noCases", {
            defaultValue: "Add at least one enabled case before running the suite.",
          })}
        </p>
      ) : null}

      {errorCode ? (
        <HostedAgentEvalRunError
          code={errorCode}
          message={errorMessage}
          retrySeconds={retrySeconds}
        />
      ) : null}

      {/* A failing read is never hidden behind the seeded `running` document. */}
      {watchedRunId && runQuery.isError ? (
        <HostedAgentEvalRunReadError
          gaveUp={pollPlan.state === "unreadable"}
          onRetry={() => void runQuery.refetch()}
        />
      ) : null}

      {pollPlan.state === "stalled" ? (
        <HostedAgentEvalRunStalled onRetry={() => void runQuery.refetch()} />
      ) : null}

      {isRunning && !run ? (
        <p
          aria-live="polite"
          className="text-muted-foreground flex items-center gap-2 text-sm"
          role="status"
        >
          <Spinner className="size-4" />
          {t("adminDashboard.hostedAgents.evals.run.starting", {
            defaultValue: "Starting the suite…",
          })}
        </p>
      ) : null}

      {run ? (
        <div className="space-y-3 border-t pt-3">
          {run.status === "running" ? (
            <p
              aria-live="polite"
              className="text-muted-foreground flex items-center gap-2 text-sm"
              role="status"
            >
              {pollPlan.state === "polling" ? <Spinner className="size-4" /> : null}
              {t("adminDashboard.hostedAgents.evals.run.inProgress", {
                defaultValue:
                  "Running — {{done}} of {{total}} cases reported. The rest have no result yet.",
                done: run.case_results.length,
                total: run.totals.cases,
              })}
            </p>
          ) : null}
          <HostedAgentEvalRunSummary run={run} />
          <HostedAgentEvalCaseResultList results={run.case_results} />
          <HostedAgentEvalProvenanceCard run={run} />
        </div>
      ) : null}
    </div>
  )
}
